import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { ref } from 'vue';

/**
 * @typedef {object} NotificationMessage
 * @property {string} default - Represents the default notification type.
 */
export const NotificationMessage = {
  default: globalTranslate('notificationMessage.default'),
};

/**
 * @typedef {object} NotificationType
 * @property {string=} success - Represents the success notification type.
 * @property {string=} info - Represents the info notification type.
 * @property {string=} warning - Represents the warning notification type.
 * @property {string=} error - Represents the error notification type.
 */
export const NotificationType = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
};

/**
 * @typedef {object} NotificationVariant
 * @property {string=} text - Represents the text notification variant.
 * @property {string=} flat - Represents the flat notification variant.
 * @property {string=} elevated - Represents the elevated notification variant.
 * @property {string=} tonal - Represents the tonal notification variant.
 * @property {string=} outlined - Represents the outlined notification variant.
 * @property {string=} plain - Represents the plain notification variant.
 */
export const NotificationVariant = {
  text: 'text',
  flat: 'flat',
  elevated: 'elevated',
  tonal: 'tonal',
  outlined: 'outlined',
  plain: 'plain',
};

/**
 * Initial configuration for a notification.
 * @typedef {object} InitialNotificationConfig
 * @property {string=} message - The initial message for the notification.
 * @property {string=} type - The initial type of the notification. Possible values: 'success', 'info', 'warning', 'error'.
 * @property {string=} variant - The initial variant of the notification. Possible values: 'text', 'flat', 'elevated', 'tonal', 'outlined', 'plain'.
 */
const initialNotificationConfig = {
  message: '',
  type: 'success',
  variant: 'elevated',
};

export const notificationConfig = ref({
  ...initialNotificationConfig,
  isShowing: false,
});

/**
 * Prepare config to reduce boilerplate code in project
 * @param {InitialNotificationConfig} config Snackbar configuration.
 * @returns {InitialNotificationConfig}
 */
function prepareConfig(config) {
  const message = config.message.message ?? config.message;

  return {
    ...config,
    message: typeof message === 'string' ? message : NotificationMessage.default,
  };
}

/**
 * Notify message to user interface.
 * @param {InitialNotificationConfig} config Snackbar configuration.
 */
function notify(config) {
  notificationConfig.value = {
    ...initialNotificationConfig,
    ...prepareConfig(config),
    isShowing: true,
  };
}

/** Notify simple success message to user interface. */
function notifyCommonSuccess() {
  notificationConfig.value = {
    ...initialNotificationConfig,
    message: globalTranslate('notificationMessage.commonSuccess'),
    isShowing: true,
  };
}

export const notificationService = {
  notify,
  notifyCommonSuccess,
};
